import { notifications } from "@mantine/notifications";
import type { DocumentItem } from "@onengine/component-library";

import type { FileManagerItem } from "@/components/core/FileManager";

const PREVIEWABLE_IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "avif", "svg"];

/**
 * The viewer's own `inferKind` treats anything that is not a PDF as an image, so a
 * non-previewable file (zip, docx) would render as a broken image rather than fail.
 * Gate on the type here and let everything else fall back to opening in a new tab.
 */
export function isPreviewableFile(item: FileManagerItem): boolean {
  if (item.type !== "FILE") return false;

  const contentType = item.contentType?.toLowerCase() ?? "";
  if (contentType.startsWith("image/") || contentType.includes("pdf")) return true;

  const extension = item.name.split(".").pop()?.toLowerCase() ?? "";
  return extension === "pdf" || PREVIEWABLE_IMAGE_EXTENSIONS.includes(extension);
}

export type ResolveFileUrl = (item: FileManagerItem) => Promise<string | null>;

export async function toDocumentItem(
  item: FileManagerItem,
  resolveFileUrl: ResolveFileUrl,
): Promise<DocumentItem | null> {
  const url = await resolveFileUrl(item);
  if (!url) return null;
  return { id: item.id, name: item.name, url, mime: item.contentType ?? undefined };
}

export async function openFileInNewTab(item: FileManagerItem, resolveFileUrl: ResolveFileUrl) {
  try {
    const url = await resolveFileUrl(item);
    if (!url) return;
    window.open(url, "_blank", "noopener");
  } catch {
    notifications.show({
      title: "Download failed",
      message: "Could not open the file. Please try again.",
      color: "red",
      autoClose: 4000,
    });
  }
}
